import { ButtonTertiary, ButtonPrimary } from "@nova/component-btn"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { formatPhoneNumber, validateEmailAddress } from "@nova/util-strings"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import store from "integrations/redux/store"
import { useState, useMemo, Dispatch, SetStateAction } from "react"
import { setGlobalNotificationContext } from "user/profile/slice"
import { pushNewList, replaceList, selectContactListById, setActiveMissionGroupId } from "./slice"
import { useContactListFunctions } from "./useContactListFunctions"
import {
    MissionGroupColor,
    missionGroupColors,
    missionGroupIcons,
    ShareContact,
    MissionGroupIcon as TMissionGroupIcon,
} from "@nova/schemas"
import { MissionGroupsAppearanceSelector } from "./MissionGroupsAppearanceSelector"
import { ContactInputMultiSelect } from "themes/default-tailwind/buttons/ContactInput"
import { Input } from "@nova/ds-component-input"

export function CreateMissionGroupModal({
    confirm,
    cancel,
}: {
    confirm: () => void
    cancel: () => void
}) {
    const dispatch = useAppDispatch()
    const { createList } = useContactListFunctions()
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [listName, setListName] = useState("")
    const [selectedEmails, setSelectedEmails] = useState<string[]>([])
    const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>([])
    const [selectedColor, setSelectedColor] = useState<MissionGroupColor>(missionGroupColors[0])
    const [selectedIcon, setSelectedIcon] = useState<TMissionGroupIcon>(missionGroupIcons[0])
    const canConfirm = useMemo(() => {
        if (!listName) return false
        if (!selectedEmails.length && !selectedPhoneNumbers.length) return false
        if (
            !selectedEmails.find((email) => validateEmailAddress(email)) &&
            !selectedPhoneNumbers.find((phoneNumber) => !!formatPhoneNumber(phoneNumber))
        )
            return false
        return true
    }, [listName, selectedEmails, selectedPhoneNumbers])
    return (
        <HighPriorityNovaModalContainer
            title="Create Mission Group"
            className="w-full max-w-[450px] max-h-screen"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={cancel} disabled={isSubmitting} />
                    <ButtonPrimary
                        title="Create"
                        onClick={async () => {
                            setIsSubmitting(true)
                            if (!workspaceId) return
                            const stagedEmails = selectedEmails
                            const stagedPhoneNumbers = selectedPhoneNumbers
                            try {
                                const res = await createList(
                                    listName,
                                    [
                                        ...stagedEmails.map(
                                            (email) =>
                                                ({
                                                    type: "email",
                                                    email,
                                                }) as ShareContact
                                        ),
                                        ...stagedPhoneNumbers.map(
                                            (phoneNumber) =>
                                                ({
                                                    type: "phone",
                                                    phoneNumber,
                                                }) as ShareContact
                                        ),
                                    ],
                                    selectedColor,
                                    selectedIcon
                                )
                                if (!res) throw new Error()
                                dispatch(pushNewList(res.contactList))
                                dispatch(setActiveMissionGroupId({ groupId: res.contactList.id }))
                                dispatch(
                                    setGlobalNotificationContext({
                                        message: `${listName} has been created.`,
                                        status: GlobalNotificationStatus.success,
                                        timeoutInMs: 3000,
                                    })
                                )
                                confirm()
                            } catch {
                                dispatch(
                                    setGlobalNotificationContext({
                                        message: "Failed to create contact list. Please try again.",
                                        status: GlobalNotificationStatus.error,
                                        timeoutInMs: 5000,
                                    })
                                )
                            } finally {
                                setIsSubmitting(false)
                            }
                        }}
                        disabled={isSubmitting || !canConfirm}
                        isLoading={isSubmitting}
                    />
                </div>
            }
            closeModal={cancel}
        >
            <MissionGroupForm
                listName={listName}
                setListName={setListName}
                selectedEmails={selectedEmails}
                setSelectedEmails={setSelectedEmails}
                selectedPhoneNumbers={selectedPhoneNumbers}
                setSelectedPhoneNumbers={setSelectedPhoneNumbers}
                selectedColor={selectedColor}
                setSelectedColor={setSelectedColor}
                selectedIcon={selectedIcon}
                setSelectedIcon={setSelectedIcon}
            />
        </HighPriorityNovaModalContainer>
    )
}

export const ManageMissionGroupModal = ({
    confirm,
    cancel,
    missionGroupId,
    initialListName,
    initialSelectedEmails,
    initialSelectedPhoneNumbers,
    initialSelectedColor,
    initialSelectedIcon,
}: {
    confirm: () => void
    cancel: () => void
    missionGroupId: string
    initialListName: string
    initialSelectedEmails: string[]
    initialSelectedPhoneNumbers: string[]
    initialSelectedColor: MissionGroupColor
    initialSelectedIcon: TMissionGroupIcon
}) => {
    const dispatch = useAppDispatch()
    const { updateList } = useContactListFunctions()
    const workspaceId = useAppSelector(selectActiveWorkspaceId())
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [listName, setListName] = useState(initialListName)
    const [selectedEmails, setSelectedEmails] = useState<string[]>(initialSelectedEmails)
    const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<string[]>(
        initialSelectedPhoneNumbers
    )
    const [selectedColor, setSelectedColor] = useState<MissionGroupColor>(initialSelectedColor)
    const [selectedIcon, setSelectedIcon] = useState<TMissionGroupIcon>(initialSelectedIcon)
    const canConfirm = useMemo(() => {
        if (!listName) return false
        if (!selectedEmails.length && !selectedPhoneNumbers.length) return false
        if (
            !selectedEmails.find((email) => validateEmailAddress(email)) &&
            !selectedPhoneNumbers.find((phoneNumber) => !!formatPhoneNumber(phoneNumber))
        )
            return false
        return true
    }, [listName, selectedEmails, selectedPhoneNumbers])
    return (
        <HighPriorityNovaModalContainer
            title="Edit Group"
            className="w-full max-w-[450px] max-h-screen"
            bottomBar={
                <div className="w-full flex justify-end items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={cancel} disabled={isSubmitting} />
                    <ButtonPrimary
                        title="Save"
                        onClick={async () => {
                            setIsSubmitting(true)
                            if (!workspaceId) return
                            const stagedEmails = selectedEmails
                            const stagedPhoneNumbers = selectedPhoneNumbers
                            const stagedContacts = [
                                ...stagedEmails.map(
                                    (email) => ({ type: "email", email }) as ShareContact
                                ),
                                ...stagedPhoneNumbers.map(
                                    (phoneNumber) =>
                                        ({ type: "phone", phoneNumber }) as ShareContact
                                ),
                            ]
                            try {
                                await updateList(
                                    missionGroupId,
                                    listName,
                                    stagedContacts,
                                    selectedColor,
                                    selectedIcon
                                )
                                const currentGroup = selectContactListById(missionGroupId)(
                                    store.getState()
                                )
                                if (currentGroup) {
                                    dispatch(
                                        replaceList({
                                            ...currentGroup,
                                            name: listName,
                                            contacts: stagedContacts,
                                            color: selectedColor,
                                            icon: selectedIcon,
                                        })
                                    )
                                }
                                dispatch(
                                    setGlobalNotificationContext({
                                        message: `${listName} has been updated.`,
                                        status: GlobalNotificationStatus.success,
                                        timeoutInMs: 3000,
                                    })
                                )
                                confirm()
                            } catch {
                                dispatch(
                                    setGlobalNotificationContext({
                                        message: "Failed to update group. Please try again.",
                                        status: GlobalNotificationStatus.error,
                                        timeoutInMs: 5000,
                                    })
                                )
                            } finally {
                                setIsSubmitting(false)
                            }
                        }}
                        disabled={isSubmitting || !canConfirm}
                        isLoading={isSubmitting}
                    />
                </div>
            }
            closeModal={cancel}
        >
            <MissionGroupForm
                listName={listName}
                setListName={setListName}
                selectedEmails={selectedEmails}
                setSelectedEmails={setSelectedEmails}
                selectedPhoneNumbers={selectedPhoneNumbers}
                setSelectedPhoneNumbers={setSelectedPhoneNumbers}
                selectedColor={selectedColor}
                setSelectedColor={setSelectedColor}
                selectedIcon={selectedIcon}
                setSelectedIcon={setSelectedIcon}
            />
        </HighPriorityNovaModalContainer>
    )
}

const MissionGroupForm = ({
    listName,
    setListName,
    selectedEmails,
    setSelectedEmails,
    selectedPhoneNumbers,
    setSelectedPhoneNumbers,
    selectedColor,
    setSelectedColor,
    selectedIcon,
    setSelectedIcon,
}: {
    listName: string
    setListName: Dispatch<SetStateAction<string>>
    selectedEmails: string[]
    setSelectedEmails: Dispatch<SetStateAction<string[]>>
    selectedPhoneNumbers: string[]
    setSelectedPhoneNumbers: Dispatch<SetStateAction<string[]>>
    selectedColor: MissionGroupColor
    setSelectedColor: Dispatch<SetStateAction<MissionGroupColor>>
    selectedIcon: TMissionGroupIcon
    setSelectedIcon: Dispatch<SetStateAction<TMissionGroupIcon>>
}) => {
    return (
        <div className="mt-4 flex flex-col gap-6">
            <Input
                value={listName}
                onChange={(e) => setListName(e.target.value)}
                placeholder="Add a name"
                label="Name*"
            />
            <div className="flex flex-col gap-2">
                <div className="text-gray-100">People*</div>
                <ContactInputMultiSelect
                    enabledFeatures={["email", "phone"]}
                    emails={selectedEmails}
                    setEmails={setSelectedEmails}
                    phoneNumbers={selectedPhoneNumbers}
                    setPhoneNumbers={setSelectedPhoneNumbers}
                    placeholder="Add names, emails, or phone numbers"
                />
            </div>
            <div className="flex flex-col gap-2">
                <div className="text-gray-200">Appearance</div>
                <MissionGroupsAppearanceSelector
                    selectedColor={selectedColor}
                    setSelectedColor={setSelectedColor}
                    selectedIcon={selectedIcon}
                    setSelectedIcon={setSelectedIcon}
                />
            </div>
        </div>
    )
}
